import React from 'react';
import { useHistory } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/Pagination';
import TableRow from '@mui/material/TableRow';
import { ArrowLeft } from 'react-feather';
import moment from 'moment';
import { formatHashValue, isEthereum } from '@/utils';
import {
    ChainId,
    NETWORK_CONFIG,
    ToChainIdPair,
    Token,
    Tokens,
} from '../../constants';

import useGetBridgeHistoryRecords, {
    RecordStatus,
} from '@/hooks/use-get-bridge-history-records';
import useGetOftBridgeHistoryRecords, {
    OftRecordStatus,
} from '@/hooks/use-get-oft-bridge-history-records';
import Loading from '@/components/Loading';

import { Info } from '@/components/Text';
import Row, { RowBetween } from '@/components/Row';
import styled from 'styled-components';
import { TYPE } from '@/theme';
import { observer } from 'mobx-react';

const HistoryWrapper = styled.div`
    display: block;
    width: 560px;
    background: ${({ theme }) => theme.c.bg05};
    margin: 0 auto;
    // margin-top: 30px;
    border-radius: 16px;
    box-sizing: border-box;
    padding: 20px 30px;
    padding-top: 0;
    padding: 24px;
    width: 100%;
    max-width: 1088px;

    @media (max-width: 768px) {
        padding: 16px;
        border-radius: 12px;
    }

    @media (max-width: 480px) {
        padding: 12px;
        border-radius: 8px;
    }

    .historyPaper {
        margin-top: 20px;
        background: ${({ theme }) => theme.c.bg04};

        @media (max-width: 768px) {
            margin-top: 16px;
        }

        .MuiTableCell-head {
            font-size: 12px;
            font-weight: 400;
            color: #a2a2a8;
            line-height: 16px;
            border-bottom: none;
            padding: 16px 0;

            @media (max-width: 768px) {
                font-size: 10px;
                padding: 12px 4px;
                line-height: 14px;
            }

            @media (max-width: 480px) {
                font-size: 9px;
                padding: 8px 2px;
                line-height: 12px;
            }
        }
        .MuiTableCell-body {
            font-size: 12px;
            font-weight: 400;
            color: ${({ theme }) => theme.c.t01};
            line-height: 16px;
            border: none;

            @media (max-width: 768px) {
                font-size: 10px;
                padding: 8px 4px;
                line-height: 14px;
            }

            @media (max-width: 480px) {
                font-size: 9px;
                padding: 6px 2px;
                line-height: 12px;
            }
        }
        .MuiPagination-root {
            display: flex;
            width: 100%;
            justify-content: center;

            @media (max-width: 768px) {
                font-size: 12px;
            }

            @media (max-width: 480px) {
                font-size: 10px;
            }
        }
        .MuiTablePagination-root {
            color: ${({ theme }) => theme.c.t01};
            font-size: 14px;
            display: flex;
            justify-content: center;
            margin-top: 10px;

            .MuiTablePagination-actions {
                margin-left: 0px;
            }

            .MuiIconButton-root.Mui-disabled {
                display: none;
            }

            @media (max-width: 768px) {
                font-size: 12px;
            }

            @media (max-width: 480px) {
                font-size: 10px;
            }
        }
        .MuiTablePagination-selectRoot {
            display: none;
        }
    }
    .historyContainer {
        width: 100%;
        overflow-x: unset;

        @media (max-width: 1024px) {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
        }

        .addressLink {
            font-size: 12px;
            font-weight: 400;
            color: ${({ theme }) => theme.c.t01};
            line-height: 16px;
            text-decoration: none;

            @media (max-width: 768px) {
                font-size: 10px;
                line-height: 14px;
            }

            @media (max-width: 480px) {
                font-size: 9px;
                line-height: 12px;
            }

            &:hover {
                text-decoration: underline;
            }
        }
        .tokenInfo {
            display: flex;
            align-items: center;
            justify-content: center;

            img {
                width: 100%;
                max-width: 24px;
                margin-right: 8px;

                @media (max-width: 768px) {
                    max-width: 20px;
                    margin-right: 6px;
                }

                @media (max-width: 480px) {
                    max-width: 16px;
                    margin-right: 4px;
                }
            }

            @media (max-width: 768px) {
                font-size: 10px;
            }

            @media (max-width: 480px) {
                font-size: 9px;
                flex-direction: column;
                gap: 2px;

                img {
                    margin-right: 0;
                    margin-bottom: 2px;
                }
            }
        }
    }
`;

const ResponsiveTableWrapper = styled.div`
    @media (max-width: 1024px) {
        .MuiTable-root {
            min-width: 900px;
        }
    }

    @media (max-width: 768px) {
        .MuiTable-root {
            min-width: 800px;
        }
    }

    @media (max-width: 480px) {
        .MuiTable-root {
            min-width: 700px;
        }
    }
`;

const HeaderWrapper = styled(RowBetween)`
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 12px;
        align-items: center;

        > div:first-child {
            align-self: flex-start;
        }

        > span:last-child {
            display: none;
        }
    }

    @media (max-width: 480px) {
        gap: 8px;

        .back-button {
            font-size: 12px;

            svg {
                width: 16px;
                height: 16px;
            }
        }

        .title {
            font-size: 24px !important;
        }
    }
`;

const DescriptionText = styled(TYPE.desc)`
    width: 100%;
    @media (max-width: 768px) {
        font-size: 11px;
    }

    @media (max-width: 480px) {
        font-size: 10px;
    }
`;

const MobileOptimizedCell = styled.span`
    display: inline-block;
    white-space: nowrap;

    @media (max-width: 768px) {
        white-space: normal;
        line-height: 1.3;
    }

    @media (max-width: 480px) {
        font-size: 9px;
        line-height: 1.2;
    }
`;

interface Column {
    id:
        | 'txid'
        | 'source'
        | 'destination'
        | 'amount'
        | 'time'
        | 'eta'
        | 'status';
    label: string;
    minWidth?: number;
}

const columns: Column[] = [
    { id: 'txid', label: 'TX ID', minWidth: 110 },
    { id: 'source', label: 'Source Chain', minWidth: 130 },
    { id: 'destination', label: 'Destination Chain', minWidth: 130 },
    { id: 'amount', label: 'Amount', minWidth: 100 },
    { id: 'time', label: 'Time', minWidth: 180 },
    { id: 'eta', label: 'ETA / Arrival Time', minWidth: 180 },
    { id: 'status', label: 'Status', minWidth: 80 },
];

function getTxLink(chainId: ChainId, tx: string) {
    const explore = NETWORK_CONFIG[chainId]?.explore;
    return `${explore}/tx/${tx}`;
}

function History() {
    const [page, setPage] = React.useState(1);
    const history = useHistory();
    // const [rowsPerPage, setRowsPerPage] = React.useState(2);
    const rowsPerPage = 10;
    const tokenMetis = Tokens[Token.metis];
    const tokenETH = Tokens[Token.eth];

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };
    const { records: standardRecords, loading: standardLoading } =
        useGetBridgeHistoryRecords();
    const { records: oftRecords, loading: oftLoading } =
        useGetOftBridgeHistoryRecords();

    // Merge standard bridge records and OFT bridge records
    const allRecords = React.useMemo(() => {
        // Transform OFT records to match the standard record format
        const transformedOftRecords = oftRecords.map((record) => ({
            id: record.id,
            token: '', // OFT doesn't have separate token address
            symbol: record.symbol,
            from: record.from,
            to: record.to,
            amount: record.amount,
            blockNumber: record.blockNumber,
            timestamp: record.timestamp,
            finishTimestamp: record.finishTimestamp,
            finishBlockNumber: record.finishBlockNumber,
            status:
                record.status === OftRecordStatus.finished
                    ? RecordStatus.finished
                    : RecordStatus.pending,
            chainId: record.sourceChainId,
            toChainId: record.destinationChainId, // Add destination chain ID
        }));

        // Combine and sort by timestamp
        return [...standardRecords, ...transformedOftRecords].sort(
            (a, b) => b.timestamp - a.timestamp,
        );
    }, [standardRecords, oftRecords]);

    const loading = standardLoading || oftLoading;
    const records = allRecords;

    // const handleChangeRowsPerPage = (
    //     event: React.ChangeEvent<HTMLInputElement>,
    // ) => {
    //     setRowsPerPage(+event.target.value);
    //     setPage(0);
    // };

    return (
        <>
            <HistoryWrapper>
                <HeaderWrapper lineHeight="none">
                    <div
                        className="back-button"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            history.push('/home');
                        }}
                    >
                        <ArrowLeft color="#9D9D9D" />
                        <TYPE.desc marginLeft={'10px'}>Back</TYPE.desc>
                    </div>
                    <TYPE.main fontSize={32} className="title">
                        Transaction History
                    </TYPE.main>
                    <span style={{ opacity: 0 }}></span>
                </HeaderWrapper>
                <Paper className={'historyPaper'}>
                    <TableContainer className={'historyContainer'}>
                        <ResponsiveTableWrapper>
                            <Table aria-label="history table">
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableCell
                                                key={column.id}
                                                align="center"
                                                style={{
                                                    width: column.minWidth,
                                                }}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {records
                                        .slice(
                                            (page - 1) * rowsPerPage,
                                            (page - 1) * rowsPerPage +
                                                rowsPerPage,
                                        )
                                        .map((row, index) => {
                                            return (
                                                <TableRow
                                                    hover
                                                    role="checkbox"
                                                    tabIndex={-1}
                                                    key={`record-${index}`}
                                                >
                                                    <TableCell align="center">
                                                        <a
                                                            className={
                                                                'addressLink'
                                                            }
                                                            href={getTxLink(
                                                                row.chainId,
                                                                row.id,
                                                            )}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {formatHashValue(
                                                                row.id,
                                                            )}
                                                        </a>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <div
                                                            className={
                                                                'tokenInfo'
                                                            }
                                                        >
                                                            <img
                                                                src={
                                                                    NETWORK_CONFIG[
                                                                        row
                                                                            .chainId
                                                                    ].img
                                                                }
                                                            />
                                                            {
                                                                NETWORK_CONFIG[
                                                                    row.chainId
                                                                ].name
                                                            }
                                                        </div>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <div
                                                            className={
                                                                'tokenInfo'
                                                            }
                                                        >
                                                            <img
                                                                src={
                                                                    NETWORK_CONFIG[
                                                                        row.toChainId ||
                                                                            ToChainIdPair[
                                                                                row
                                                                                    .chainId
                                                                            ]
                                                                    ].img
                                                                }
                                                            />
                                                            {
                                                                NETWORK_CONFIG[
                                                                    row.toChainId ||
                                                                        ToChainIdPair[
                                                                            row
                                                                                .chainId
                                                                        ]
                                                                ].name
                                                            }
                                                        </div>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {row.amount}{' '}
                                                        {row.symbol}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <MobileOptimizedCell>
                                                            {moment(
                                                                row.timestamp,
                                                            ).format(
                                                                'MMM DD, YYYY HH:mm:ss (UTC Z)',
                                                            )}
                                                        </MobileOptimizedCell>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {row.status ===
                                                            RecordStatus.finished &&
                                                        row.finishTimestamp ? (
                                                            <MobileOptimizedCell>
                                                                {moment(
                                                                    row.finishTimestamp,
                                                                ).format(
                                                                    'MMM DD, YYYY HH:mm:ss (UTC Z)',
                                                                )}
                                                            </MobileOptimizedCell>
                                                        ) : isEthereum(
                                                              row.chainId,
                                                          ) ? (
                                                            '-'
                                                        ) : row.status === 0 ? (
                                                            <MobileOptimizedCell
                                                                style={{
                                                                    letterSpacing:
                                                                        '-0.5px',
                                                                }}
                                                            >
                                                                Estimated time
                                                                of arrival is
                                                                before
                                                                <br />
                                                                {moment
                                                                    .utc(
                                                                        row.timestamp,
                                                                    )
                                                                    .add(
                                                                        8,
                                                                        'days',
                                                                    )
                                                                    .format(
                                                                        'MMM DD, YYYY',
                                                                    )}
                                                            </MobileOptimizedCell>
                                                        ) : (
                                                            <MobileOptimizedCell>
                                                                {moment(
                                                                    row.timestamp,
                                                                )
                                                                    .add(
                                                                        8,
                                                                        'days',
                                                                    )
                                                                    .format(
                                                                        'MMM DD, YYYY HH:mm:ss (UTC Z)',
                                                                    )}
                                                            </MobileOptimizedCell>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {row.status === 0 ? (
                                                            <span
                                                                style={{
                                                                    color: '#65dacf',
                                                                }}
                                                            >
                                                                Pending
                                                            </span>
                                                        ) : (
                                                            'Finish'
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    {!loading && !records.length && (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Row align="center">
                                                    <Info>No records</Info>
                                                </Row>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </ResponsiveTableWrapper>
                    </TableContainer>
                    {loading ? (
                        <Loading
                            style={{ display: 'block', margin: '0 auto' }}
                        />
                    ) : records.length ? (
                        <TablePagination
                            // rowsPerPageOptions={[10, 25, 100]}
                            count={Math.floor(
                                records.length > rowsPerPage
                                    ? records.length / rowsPerPage + 1
                                    : 1,
                            )}
                            page={page}
                            onChange={handleChangePage}
                            // onChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                    ) : null}
                    <Row
                        justify="center"
                        style={{ width: '100%', padding: '12px' }}
                    >
                        <DescriptionText textAlign="center">
                            Only the lastest 100 deposit/withdrawal records are
                            showing here.
                        </DescriptionText>
                    </Row>
                </Paper>
            </HistoryWrapper>
        </>
    );
}

export default observer(History);
